<template>
  <div class="site-footer critical-component-clear">
    <div
      class="
          site-footer__row
          site-footer__row--padding-large
          grid
          container
        "
    >
      <accordion-snippet
        v-if="isMobile"
        block-class="site-footer"
        class="col xs-span"
        heading-type="h2"
        :panel-data="linkListAccordion"
        title-class="text-body-m-bold"
      />

      <template v-else>
        <div
          v-for="block of liquid.blocks"
          :key="block.title"
          class="col xs-span l2"
        >
          <p class="site-footer__linklist-title text-body-m-bold">
            <strong
              class="text-body-m-bold"
              v-text="block.title"
            />
          </p>

          <ul class="site-footer__linklist">
            <li
              v-for="link of block.linkList"
              :key="link.title"
            >
              <a
                :aria-current="link.active"
                class="site-footer__link text-body-m-regular"
                :href="link.url"
                v-text="link.title"
              />
            </li>
          </ul>
        </div>
      </template>

      <section class="col xs-span l9-12">
        <newsletter-form
          block-class="site-footer"
          :classes="liquid.newsletter.classes"
          :color-mode="liquid.newsletter.colorMode"
          :content="{
            copy: liquid.newsletter.copy,
            title: liquid.newsletter.title,
            textColour: liquid.newsletter.textColour
          }"
          hide-label
          :namespace="liquid.sectionId"
        />

        <p
          class="site-footer__social-text text-body-xs-regular"
          v-text="$string('general.site_footer.social_media')"
        />

        <div v-html="liquid.socialLinks" />
      </section>
    </div>

    <div class="site-footer__row site-footer__row--padding-small">
      <div class="grid container">
        <div class="site-footer__legal col xs-span l8">
          <p class="site-footer__copyright text-body-xs-regular">
            <icon-brand-logo />
            <span v-html="liquid.copyright" />
          </p>

          <ul class="site-footer__linklist site-footer__policies">
            <li
              v-for="link of liquid.policyMenu"
              :key="link.title"
              class="text-body-xs-regular"
            >
              <a
                :aria-current="link.active"
                class="site-footer__link text-body-xs-regular"
                :href="getPolicyLink(link.url)"
                v-text="link.title"
              />
            </li>
          </ul>
        </div>

        <div
          class="site-footer__payment-icons-container col xs-span l9-12"
          v-html="liquid.paymentIcons"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site footer component with social links.
 * - Dynamic section.
 *
 * @param {Object} liquid - Formatted data from Shopify.
 * @param {Array} liquid.blocks - Menu linklist.
 * @param {String} liquid.copyright - Copyright string.
 * @param {String} liquid.paymentIcons - Captured payment icons snippet.
 * @param {Array} liquid.policyMenu - Policy linklist.
 * @param {String} liquid.sectionId - Shopify section ID.
 * @param {String} liquid.socialLinks - Captured social media icons snippet.
 *
 */
import { mapState } from 'vuex'

import AccordionSnippet from '~async/accordion-snippet/accordion-snippet'
import IconBrandLogo from '~icons/general/brand-logo.svg'
import NewsletterForm from '~async/newsletter-form/newsletter-form'

export default {
  name: 'SiteFooter',

  components: {
    AccordionSnippet,
    IconBrandLogo,
    NewsletterForm,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blocks: [],
        copyright: '',
        newsletter: {
          classes: {},
          colorMode: 'dark',
          copy: '',
          title: '',
          textColour: '',
        },
        paymentIcons: '',
        policyMenu: [],
        socialLinks: '',
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return formatted HTML of footer linklists for accordion.
     * @returns {String}
     */
    linkListAccordion() {
      return this.liquid.blocks.map((block) => {
        const formattedBody = `
          <ul class="site-footer__linklist">
            ${this.getLinklistLink(block.linkList)}
          </ul>
        `

        return {
          title: block.title,
          content: formattedBody,
        }
      })
    },
  },

  methods: {

    /**
     * Return a formatted series of `<li>` elements containing anchor links.
     * @param {Array} linklist - Footer linklist.
     * @returns {String}
     */
    getLinklistLink(linklist) {
      let column = ''

      linklist.forEach(link => {
        const listitem = `
          <li>
            <a
              aria-current="${link.active}"
              class="text-body-m-regular site-footer__link"
              href="${link.url}"
            >
              ${link.title}
            </a>
          </li>
        `

        column = column.concat(' ', listitem)
      })

      return column
    },

    /**
     * Return policy link.
     * @param {String} url - Policy url.
     * @returns {String}
     */
    getPolicyLink(url) {
      return url.includes('#') ? url.replace('#', '') : url
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
